import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Spin,
  Table,
} from 'antd';
import {
  CopyOutlined,
  DownloadOutlined,
  FileZipOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { getRequest, ResponseDataType } from 'api';
import { ClassLevelSelect } from 'Components';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import {
  BaseStudentBillItem,
  getGroupSelectionState,
  groupBillsByStudent,
  StudentBillGroup,
  toggleGroupSelectionKeys,
} from '../List/Tabs/studentBillGrouping';
import {
  buildStudentSlipFileName,
  copySlipBlobToClipboard,
  createZipBlobFromFiles,
  downloadBlobAsFile,
  QrSlipRenderData,
  renderQrPaymentSlipToBlob,
  sanitizeFileNameSegment,
  ZipEntryFile,
} from './qrSlipRenderer';
import './QrPaymentSlip.css';

const { Option } = Select;

type ResponseListDataType<T> = ResponseDataType<{
  list?: T[];
  data?: T[];
  total: number;
}>;

interface InvoiceRecord extends BaseStudentBillItem {
  studentInvoiceId: number;
  invoidId: string;
  billId: string;
  content: string;
  description: string;
  studentId: number;
  studentName: string;
  studentCode: string;
  schoolMst: string;
  cost: number;
  status: number;
  month: number;
  year: number;
  classYearId: number;
  className: string;
  classYearName?: string;
  schoolYear: string;
  paymentDate: string | null;
}

interface ClassYearItem {
  classYearId: number;
  className: string;
  classLevel: string;
  schoolYear: string;
}

const formatCurrency = (val: number): string =>
  `${new Intl.NumberFormat('vi-VN').format(val || 0)} VNĐ`;

const buildQrCacheKey = (studentId: number, invoiceIds: number[]): string => {
  const sortedIds = [...invoiceIds].sort((a, b) => a - b);
  return `${studentId}:${sortedIds.join(',')}`;
};

export const QrPaymentSlipPage: React.FC = () => {
  const schoolIdStr = localStorage.getItem('schoolId');
  const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;
  const schoolName =
    localStorage.getItem('schoolName') || 'HỆ THỐNG QUẢN LÝ HỌC PHÍ VSCHOOL';

  const [selectedMonthYear, setSelectedMonthYear] = useState<Dayjs>(() => {
    const { month, year } = getStoredWorkMonth();
    return dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
  });
  const [selectedClassLevel, setSelectedClassLevel] = useState<
    string | undefined
  >(undefined);
  const [selectedClassId, setSelectedClassId] = useState<number | undefined>(
    undefined
  );
  const [studentNameInput, setStudentNameInput] = useState<string>('');
  const [studentNameSearch, setStudentNameSearch] = useState<string>('');

  const [classList, setClassList] = useState<ClassYearItem[]>([]);
  const [loadingClasses, setLoadingClasses] = useState<boolean>(false);

  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(100);
  const [totalStudentGroups, setTotalStudentGroups] = useState<number>(0);

  // Selected studentInvoiceIds (starts EMPTY — not auto-checked!)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [activeStudentKey, setActiveStudentKey] = useState<string | null>(null);

  // Generated QR base64 map by key `${studentId}:${sortedInvoiceIds.join(',')}`
  const [qrMap, setQrMap] = useState<Record<string, string>>({});
  const [generatingKey, setGeneratingKey] = useState<string | null>(null);
  const [activeQrError, setActiveQrError] = useState<string | null>(null);
  const [processingSingleAction, setProcessingSingleAction] =
    useState<boolean>(false);

  const [batchModalOpen, setBatchModalOpen] = useState<boolean>(false);
  const [batchProgressText, setBatchProgressText] = useState<string>('');
  const [batchCompletedCount, setBatchCompletedCount] = useState<number>(0);
  const [batchTotalCount, setBatchTotalCount] = useState<number>(0);
  const [batchRunning, setBatchRunning] = useState<boolean>(false);

  useEffect(() => {
    const handleWorkMonthChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ month: number; year: number }>;
      if (customEvent.detail?.month && customEvent.detail?.year) {
        const { month, year } = customEvent.detail;
        setSelectedMonthYear(
          dayjs(`${year}-${String(month).padStart(2, '0')}-01`)
        );
        setPage(1);
      }
    };
    window.addEventListener('workMonthChanged', handleWorkMonthChange);
    return () =>
      window.removeEventListener('workMonthChanged', handleWorkMonthChange);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStudentNameSearch(studentNameInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [studentNameInput]);

  useEffect(() => {
    if (!schoolId) return;
    const fetchClasses = async () => {
      setLoadingClasses(true);
      try {
        const res: ResponseListDataType<ClassYearItem> = await getRequest(
          `/cms/class/get-class-years-by-start-end-schoolId?schoolId=${schoolId}&start=${selectedMonthYear.year()}&page=1&perPage=1000`
        );
        const clsList = res?.info?.list || res?.info?.data;
        if (res?.code === 200 && Array.isArray(clsList)) {
          setClassList(clsList);
        }
      } catch {
        // ignore
      } finally {
        setLoadingClasses(false);
      }
    };
    fetchClasses();
  }, [schoolId, selectedMonthYear]);

  const filteredClassList = useMemo(() => {
    if (!selectedClassLevel) return classList;
    return classList.filter(
      (c) => String(c.classLevel || '').trim() === selectedClassLevel.trim()
    );
  }, [classList, selectedClassLevel]);

  const fetchUnpaidInvoices = useCallback(async () => {
    if (!schoolId || !selectedClassId) {
      setInvoices([]);
      setTotalStudentGroups(0);
      setSelectedRowKeys([]);
      return;
    }
    setLoadingInvoices(true);
    try {
      const month = selectedMonthYear.month() + 1;
      const year = selectedMonthYear.year();
      const params = new URLSearchParams({
        schoolId: String(schoolId),
        month: String(month),
        year: String(year),
        status: '0',
        classId: String(selectedClassId),
        page: String(page),
        perPage: String(perPage),
        groupByStudent: 'true',
      });
      if (selectedClassLevel) {
        params.append('classLevel', selectedClassLevel);
      }
      if (studentNameSearch) {
        params.append('studentName', studentNameSearch);
      }

      const res: ResponseListDataType<InvoiceRecord> = await getRequest(
        `/cms/invoice/list-invoices?${params.toString()}`
      );
      if (res?.code === 200 && res.info) {
        const rawList = res.info.list || res.info.data;
        const list = Array.isArray(rawList) ? rawList : [];
        setInvoices(list);
        setTotalStudentGroups(res.info.total ?? 0);
      } else {
        setInvoices([]);
        setTotalStudentGroups(0);
      }
    } catch {
      toast.error('Không thể tải danh sách biên lai');
      setInvoices([]);
      setTotalStudentGroups(0);
    } finally {
      setLoadingInvoices(false);
    }
  }, [
    schoolId,
    selectedMonthYear,
    selectedClassId,
    selectedClassLevel,
    studentNameSearch,
    page,
    perPage,
  ]);

  useEffect(() => {
    fetchUnpaidInvoices();
    setSelectedRowKeys([]);
  }, [fetchUnpaidInvoices]);

  const studentGroups = useMemo<StudentBillGroup<InvoiceRecord>[]>(
    () => groupBillsByStudent(invoices),
    [invoices]
  );

  useEffect(() => {
    const allKeys = studentGroups.map((g) => g.groupKey);
    setActiveStudentKey((prev) =>
      prev && allKeys.includes(prev) ? prev : allKeys[0] || null
    );
    setActiveQrError(null);
  }, [studentGroups]);

  const activeStudentGroup = useMemo(
    () =>
      studentGroups.find((g) => g.groupKey === activeStudentKey) ||
      studentGroups[0] ||
      null,
    [studentGroups, activeStudentKey]
  );

  // Bills for a single student action/preview:
  // If user checked specific bills of this student, use those; otherwise use all unpaid bills of this student.
  const getTargetBillsForStudent = useCallback(
    (group: StudentBillGroup<InvoiceRecord>): InvoiceRecord[] => {
      const checkedBills = group.bills.filter((b) =>
        selectedRowKeys.includes(b.studentInvoiceId)
      );
      return checkedBills.length > 0 ? checkedBills : group.bills;
    },
    [selectedRowKeys]
  );

  const getCachedQrForGroup = useCallback(
    (group: StudentBillGroup<InvoiceRecord> | null): string | null => {
      if (!group) return null;
      const targetBills = getTargetBillsForStudent(group);
      if (targetBills.length === 0) return null;
      const cacheKey = buildQrCacheKey(
        group.studentId,
        targetBills.map((b) => b.studentInvoiceId)
      );
      return qrMap[cacheKey] || null;
    },
    [getTargetBillsForStudent, qrMap]
  );

  const generateQrForStudentBills = useCallback(
    async (studentId: number, invoiceIds: number[]): Promise<string> => {
      const cacheKey = buildQrCacheKey(studentId, invoiceIds);
      if (qrMap[cacheKey]) {
        return qrMap[cacheKey];
      }
      const sortedIds = [...invoiceIds].sort((a, b) => a - b);
      const res: ResponseDataType<string> = await getRequest(
        `/cms/accountant/generate-qr-payment?studentId=${studentId}&listInvoiceIds=${sortedIds.join(
          ','
        )}`
      );
      if (res?.code === 200 && res.info) {
        const qrBase64 = res.info;
        setQrMap((prev) => ({ ...prev, [cacheKey]: qrBase64 }));
        return qrBase64;
      }
      const errMsg = Array.isArray(res?.msg) ? res.msg.join(', ') : res?.msg;
      throw new Error(errMsg || 'Không thể tạo mã QR');
    },
    [qrMap]
  );

  const handleGenerateQrForSingleGroup = async (
    group: StudentBillGroup<InvoiceRecord>
  ) => {
    setActiveStudentKey(group.groupKey);
    const targetBills = getTargetBillsForStudent(group);
    if (targetBills.length === 0) return;

    setGeneratingKey(group.groupKey);
    setActiveQrError(null);
    try {
      await generateQrForStudentBills(
        group.studentId,
        targetBills.map((b) => b.studentInvoiceId)
      );
      toast.success(`Đã tạo QR: ${group.studentName}`);
    } catch (err: any) {
      const msg = err?.message || 'Lỗi khi tạo mã QR';
      setActiveQrError(msg);
      toast.error(msg);
    } finally {
      setGeneratingKey(null);
    }
  };

  const buildSlipRenderData = useCallback(
    (
      group: StudentBillGroup<InvoiceRecord>,
      chosenBills: InvoiceRecord[],
      qrBase64: string
    ): QrSlipRenderData => ({
      schoolName,
      studentId: group.studentId,
      studentName: group.studentName,
      studentCode: group.studentCode,
      classYearName: group.className,
      schoolYear: group.schoolYear,
      month: chosenBills[0]?.month || selectedMonthYear.month() + 1,
      year: chosenBills[0]?.year || selectedMonthYear.year(),
      bills: chosenBills.map((b) => ({
        studentInvoiceId: b.studentInvoiceId,
        invoidId: b.invoidId,
        billId: b.billId,
        content: b.content,
        cost: b.cost,
        month: b.month,
        year: b.year,
      })),
      totalCost: chosenBills.reduce((s, b) => s + (b.cost || 0), 0),
      qrBase64,
    }),
    [schoolName, selectedMonthYear]
  );

  const handleCopySingleSlip = async (
    group: StudentBillGroup<InvoiceRecord>
  ) => {
    setActiveStudentKey(group.groupKey);
    const targetBills = getTargetBillsForStudent(group);
    if (targetBills.length === 0) return;

    setProcessingSingleAction(true);
    try {
      const qr = await generateQrForStudentBills(
        group.studentId,
        targetBills.map((b) => b.studentInvoiceId)
      );
      const slipData = buildSlipRenderData(group, targetBills, qr);
      const blob = await renderQrPaymentSlipToBlob(slipData);
      await copySlipBlobToClipboard(blob);
      toast.success(`Đã sao chép ảnh QR của ${group.studentName}`);
    } catch (err: any) {
      toast.error(err?.message || 'Không thể sao chép ảnh');
    } finally {
      setProcessingSingleAction(false);
    }
  };

  const handleDownloadSingleSlip = async (
    group: StudentBillGroup<InvoiceRecord>
  ) => {
    setActiveStudentKey(group.groupKey);
    const targetBills = getTargetBillsForStudent(group);
    if (targetBills.length === 0) return;

    setProcessingSingleAction(true);
    try {
      const qr = await generateQrForStudentBills(
        group.studentId,
        targetBills.map((b) => b.studentInvoiceId)
      );
      const slipData = buildSlipRenderData(group, targetBills, qr);
      const blob = await renderQrPaymentSlipToBlob(slipData);
      downloadBlobAsFile(blob, buildStudentSlipFileName(slipData));
    } catch (err: any) {
      toast.error(err?.message || 'Không thể tải ảnh');
    } finally {
      setProcessingSingleAction(false);
    }
  };

  // Selection logic (identical to UnpaidBillList)
  const allPageInvoiceIds = useMemo(
    () => invoices.map((item) => item.studentInvoiceId),
    [invoices]
  );

  const isPageAllChecked =
    allPageInvoiceIds.length > 0 &&
    allPageInvoiceIds.every((id) => selectedRowKeys.includes(id));

  const isPageIndeterminate =
    !isPageAllChecked &&
    allPageInvoiceIds.some((id) => selectedRowKeys.includes(id));

  const handleToggleSelectAllPage = () => {
    if (isPageAllChecked) {
      const pageSet = new Set<React.Key>(allPageInvoiceIds);
      setSelectedRowKeys((prev) => prev.filter((k) => !pageSet.has(k)));
    } else {
      const nextSet = new Set<React.Key>(selectedRowKeys);
      for (const id of allPageInvoiceIds) {
        nextSet.add(id);
      }
      setSelectedRowKeys(Array.from(nextSet));
    }
  };

  const selectedGroupsToExport = useMemo(
    () =>
      studentGroups
        .map((group) => ({
          group,
          bills: group.bills.filter((b) =>
            selectedRowKeys.includes(b.studentInvoiceId)
          ),
        }))
        .filter((item) => item.bills.length > 0),
    [studentGroups, selectedRowKeys]
  );

  const handleBatchGenerateQrOnly = async () => {
    if (selectedGroupsToExport.length === 0) return;
    setBatchModalOpen(true);
    setBatchRunning(true);
    setBatchCompletedCount(0);
    setBatchTotalCount(selectedGroupsToExport.length);

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < selectedGroupsToExport.length; i++) {
      const { group, bills } = selectedGroupsToExport[i];
      setBatchProgressText(
        `Đang tạo QR: ${group.studentName} (${i + 1}/${
          selectedGroupsToExport.length
        })`
      );
      try {
        await generateQrForStudentBills(
          group.studentId,
          bills.map((b) => b.studentInvoiceId)
        );
        successCount++;
      } catch {
        failedCount++;
      }
      setBatchCompletedCount(i + 1);
    }

    setBatchRunning(false);
    setBatchModalOpen(false);

    if (successCount > 0 && failedCount === 0) {
      toast.success(`Đã tạo QR cho ${successCount} học sinh`);
    } else if (successCount > 0) {
      toast.warning(
        `Đã tạo QR ${successCount} học sinh (${failedCount} lỗi)`
      );
    } else {
      toast.error('Không thể tạo mã QR');
    }
  };

  const handleBatchExportZip = async () => {
    if (selectedGroupsToExport.length === 0) return;
    setBatchModalOpen(true);
    setBatchRunning(true);
    setBatchCompletedCount(0);
    setBatchTotalCount(selectedGroupsToExport.length);

    const zipEntries: ZipEntryFile[] = [];
    let failedCount = 0;

    for (let i = 0; i < selectedGroupsToExport.length; i++) {
      const { group, bills } = selectedGroupsToExport[i];
      setBatchProgressText(
        `Đang xuất: ${group.studentName} (${i + 1}/${
          selectedGroupsToExport.length
        })`
      );
      try {
        const qr = await generateQrForStudentBills(
          group.studentId,
          bills.map((b) => b.studentInvoiceId)
        );
        const slipData = buildSlipRenderData(group, bills, qr);
        const pngBlob = await renderQrPaymentSlipToBlob(slipData);
        const arrayBuffer = await pngBlob.arrayBuffer();
        zipEntries.push({
          fileName: buildStudentSlipFileName(slipData, i),
          data: new Uint8Array(arrayBuffer),
        });
      } catch {
        failedCount++;
      }
      setBatchCompletedCount(i + 1);
    }

    setBatchRunning(false);
    setBatchModalOpen(false);

    if (zipEntries.length === 0) {
      toast.error('Không thể xuất file ZIP');
      return;
    }

    const zipBlob = createZipBlobFromFiles(zipEntries);
    const monthStr = String(selectedMonthYear.month() + 1).padStart(2, '0');
    const yearStr = String(selectedMonthYear.year());
    const classObj = classList.find((c) => c.classYearId === selectedClassId);
    const classLabel = classObj
      ? sanitizeFileNameSegment(classObj.className)
      : 'Lop';
    downloadBlobAsFile(
      zipBlob,
      `QR_${classLabel}_T${monthStr}-${yearStr}_${zipEntries.length}HS.zip`
    );

    if (failedCount > 0) {
      toast.warning(`Đã xuất ${zipEntries.length} HS (${failedCount} lỗi)`);
    } else {
      toast.success(`Đã tải file ZIP (${zipEntries.length} học sinh)`);
    }
  };

  const activeTargetBills = useMemo(
    () =>
      activeStudentGroup ? getTargetBillsForStudent(activeStudentGroup) : [],
    [activeStudentGroup, getTargetBillsForStudent]
  );

  const activeTotalAmount = useMemo(
    () => activeTargetBills.reduce((s, b) => s + (b.cost || 0), 0),
    [activeTargetBills]
  );

  const activeQrBase64 = useMemo(
    () => getCachedQrForGroup(activeStudentGroup),
    [getCachedQrForGroup, activeStudentGroup]
  );

  const isGeneratingActiveStudent =
    Boolean(activeStudentGroup) &&
    generatingKey === activeStudentGroup?.groupKey;

  const columns: ColumnsType<StudentBillGroup<InvoiceRecord>> = [
    {
      title: (
        <Checkbox
          checked={isPageAllChecked}
          indeterminate={isPageIndeterminate}
          disabled={allPageInvoiceIds.length === 0}
          onChange={handleToggleSelectAllPage}
        />
      ),
      key: 'select',
      width: 42,
      align: 'center',
      render: (_, group) => {
        const state = getGroupSelectionState(group.bills, selectedRowKeys);
        return (
          <Checkbox
            checked={state.checked}
            indeterminate={state.indeterminate}
            onChange={() =>
              setSelectedRowKeys((prev) =>
                toggleGroupSelectionKeys(group.bills, prev)
              )
            }
          />
        );
      },
    },
    {
      title: 'STT',
      key: 'stt',
      width: 48,
      align: 'center',
      render: (_, __, idx) => (page - 1) * perPage + idx + 1,
    },
    {
      title: 'Họ tên học sinh',
      dataIndex: 'studentName',
      key: 'studentName',
      render: (name: string) => (
        <span style={{ fontWeight: 600 }}>{name}</span>
      ),
    },
    {
      title: 'Mã HS',
      dataIndex: 'studentCode',
      key: 'studentCode',
      width: 120,
      render: (code: string, record) => code || record.studentId,
    },
    {
      title: 'Lớp',
      dataIndex: 'className',
      key: 'className',
      width: 72,
      align: 'center',
    },
    {
      title: 'Số biên lai',
      key: 'billCount',
      width: 95,
      align: 'center',
      render: (_, record) => {
        const checkedCount = record.bills.filter((b) =>
          selectedRowKeys.includes(b.studentInvoiceId)
        ).length;
        return checkedCount > 0
          ? `${checkedCount}/${record.billCount}`
          : record.billCount;
      },
    },
    {
      title: 'Số tiền',
      key: 'totalAmount',
      width: 135,
      align: 'right',
      render: (_, record) => {
        const targetBills = getTargetBillsForStudent(record);
        const sum = targetBills.reduce((acc, b) => acc + (b.cost || 0), 0);
        return (
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(sum)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="qr-slip-page">
      <div className="qr-slip-toolbar">
        <div className="qr-slip-filters">
          <DatePicker
            picker="month"
            format="MM/YYYY"
            allowClear={false}
            value={selectedMonthYear}
            onChange={(val) => {
              if (val) {
                setSelectedMonthYear(val);
                setPage(1);
              }
            }}
            style={{ width: 125 }}
          />
          <ClassLevelSelect
            value={selectedClassLevel}
            onChange={(val) => {
              setSelectedClassLevel(val);
              setSelectedClassId(undefined);
              setPage(1);
            }}
            allowClear
            placeholder="Khối"
            style={{ width: 135 }}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            loading={loadingClasses}
            placeholder="Chọn lớp *"
            value={selectedClassId}
            onChange={(val) => {
              setSelectedClassId(val);
              setPage(1);
            }}
            style={{ width: 180 }}
          >
            {filteredClassList.map((cls) => (
              <Option key={cls.classYearId} value={cls.classYearId}>
                {cls.className}
              </Option>
            ))}
          </Select>
          <Input
            allowClear
            disabled={!selectedClassId}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="Tìm học sinh"
            value={studentNameInput}
            onChange={(e) => setStudentNameInput(e.target.value)}
            style={{ width: 180 }}
          />
          <Button
            icon={<ReloadOutlined />}
            disabled={!selectedClassId}
            onClick={fetchUnpaidInvoices}
            loading={loadingInvoices}
          />
        </div>

        <div className="qr-slip-actions">
          <Button
            icon={<QrcodeOutlined />}
            disabled={selectedGroupsToExport.length === 0}
            onClick={handleBatchGenerateQrOnly}
          >
            Tạo QR ({selectedGroupsToExport.length})
          </Button>
          <Button
            type="primary"
            icon={<FileZipOutlined />}
            disabled={selectedGroupsToExport.length === 0}
            onClick={handleBatchExportZip}
          >
            Xuất ZIP ({selectedGroupsToExport.length})
          </Button>
        </div>
      </div>

      {!selectedClassId ? (
        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            padding: '48px 16px',
          }}
        >
          <Empty description="Vui lòng chọn lớp học" />
        </div>
      ) : (
        <div className="qr-slip-workspace">
          <div>
            <Table<StudentBillGroup<InvoiceRecord>>
              rowKey="groupKey"
              size="small"
              bordered
              className="qr-slip-table"
              loading={loadingInvoices}
              columns={columns}
              dataSource={studentGroups}
              rowClassName={(record, index) => {
                const isFocused = activeStudentGroup?.groupKey === record.groupKey;
                const zebraClass = index % 2 === 0 ? 'qr-slip-row-even' : 'qr-slip-row-odd';
                return isFocused ? `${zebraClass} qr-slip-active-row` : zebraClass;
              }}
              onRow={(record) => ({
                onClick: () => {
                  setActiveStudentKey(record.groupKey);
                  setActiveQrError(null);
                },
              })}
              expandable={{
                rowExpandable: (record) => record.billCount > 1,
                expandedRowRender: (group) => {
                  const groupSelectedKeys = group.bills
                    .filter((b) => selectedRowKeys.includes(b.studentInvoiceId))
                    .map((b) => b.studentInvoiceId);

                  return (
                    <div className="qr-slip-subtable-container">
                      <Table<InvoiceRecord>
                        rowKey="studentInvoiceId"
                        size="small"
                        bordered
                        pagination={false}
                        dataSource={group.bills}
                        rowSelection={{
                          selectedRowKeys: groupSelectedKeys,
                          onChange: (nextSubKeys) => {
                            const groupIdsSet = new Set<React.Key>(
                              group.bills.map((b) => b.studentInvoiceId)
                            );
                            setSelectedRowKeys((prev) => {
                              const filtered = prev.filter(
                                (k) => !groupIdsSet.has(k)
                              );
                              return [...filtered, ...nextSubKeys];
                            });
                          },
                        }}
                        columns={[
                          {
                            title: 'Mã biên lai',
                            dataIndex: 'invoidId',
                            key: 'invoidId',
                            width: 130,
                            render: (val: string, b) => val || b.billId || '—',
                          },
                          {
                            title: 'Khoản thu',
                            dataIndex: 'content',
                            key: 'content',
                          },
                          {
                            title: 'Số tiền',
                            dataIndex: 'cost',
                            key: 'cost',
                            width: 130,
                            align: 'right',
                            render: (val: number) => (
                              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {formatCurrency(val)}
                              </span>
                            ),
                          },
                        ]}
                      />
                    </div>
                  );
                },
              }}
              pagination={{
                current: page,
                pageSize: perPage,
                total: totalStudentGroups,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                showTotal: (total) => `Tổng ${total} học sinh`,
                onChange: (nextPage, nextPerPage) => {
                  setPage(nextPage);
                  setPerPage(nextPerPage || 100);
                },
              }}
            />
          </div>

          <div className="qr-slip-preview-panel">
            <div className="qr-slip-preview-header">
              <span className="qr-slip-preview-title">Phiếu báo thu</span>
              {activeStudentGroup && (
                <Space size={6}>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CopyOutlined />}
                    loading={processingSingleAction}
                    disabled={activeTargetBills.length === 0}
                    onClick={() => handleCopySingleSlip(activeStudentGroup)}
                  >
                    Copy ảnh
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={processingSingleAction}
                    disabled={activeTargetBills.length === 0}
                    onClick={() => handleDownloadSingleSlip(activeStudentGroup)}
                  >
                    Tải PNG
                  </Button>
                </Space>
              )}
            </div>

            {!activeStudentGroup ? (
              <Empty
                description="Không có dữ liệu"
                style={{ padding: '36px 0' }}
              />
            ) : (
              <div className="qr-slip-card">
                <div className="qr-slip-card-title">
                  PHIẾU BÁO THU HỌC PHÍ — THÁNG{' '}
                  {String(
                    activeTargetBills[0]?.month ||
                      selectedMonthYear.month() + 1
                  ).padStart(2, '0')}
                  /{activeTargetBills[0]?.year || selectedMonthYear.year()}
                </div>

                <div className="qr-slip-card-body">
                  <div className="qr-slip-student-meta">
                    <div>
                      <strong>Học sinh:</strong> {activeStudentGroup.studentName}
                    </div>
                    <div>
                      <strong>Lớp:</strong> {activeStudentGroup.className || '—'}
                      {' | '}
                      <strong>Mã HS:</strong>{' '}
                      {activeStudentGroup.studentCode ||
                        activeStudentGroup.studentId}
                    </div>
                  </div>

                  <table className="qr-slip-fee-table">
                    <thead>
                      <tr>
                        <th style={{ width: 38, textAlign: 'center' }}>STT</th>
                        <th>Khoản thu</th>
                        <th style={{ width: 120, textAlign: 'right' }}>
                          Số tiền
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeTargetBills.map((bill, idx) => (
                        <tr key={bill.studentInvoiceId}>
                          <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                          <td>{bill.content || bill.invoidId}</td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {formatCurrency(bill.cost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}>Tổng cộng</td>
                        <td
                          style={{
                            textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatCurrency(activeTotalAmount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                  <div className="qr-slip-qr-area">
                    {isGeneratingActiveStudent ? (
                      <div className="qr-slip-qr-frame">
                        <Spin />
                      </div>
                    ) : activeQrBase64 ? (
                      <div className="qr-slip-qr-frame">
                        <img
                          src={
                            activeQrBase64.startsWith('data:image')
                              ? activeQrBase64
                              : `data:image/png;base64,${activeQrBase64}`
                          }
                          alt="QR"
                        />
                      </div>
                    ) : (
                      <div style={{ padding: '16px 0' }}>
                        <Button
                          type="primary"
                          icon={<QrcodeOutlined />}
                          onClick={() =>
                            handleGenerateQrForSingleGroup(activeStudentGroup)
                          }
                        >
                          Tạo mã QR
                        </Button>
                        {activeQrError && (
                          <Alert
                            type="error"
                            showIcon
                            message={activeQrError}
                            style={{ marginTop: 10, textAlign: 'left' }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        title="Đang xử lý"
        open={batchModalOpen}
        closable={!batchRunning}
        maskClosable={false}
        footer={null}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ marginBottom: 8 }}>{batchProgressText}</div>
          <Progress
            percent={
              batchTotalCount > 0
                ? Math.round((batchCompletedCount / batchTotalCount) * 100)
                : 0
            }
            status="active"
          />
        </div>
      </Modal>
    </div>
  );
};

export default QrPaymentSlipPage;
